/* eslint-disable max-lines */
import { format, parseISO } from 'date-fns';
import { find, flatMap, includes, isDate, lowerCase, omit, pick, toNumber } from 'lodash';
import PropTypes from 'prop-types';
import React, { forwardRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Slide,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { lighten, useTheme } from '@mui/material/styles';
import Fuse from 'fuse.js';

import { closeEScriptRequests, importEScriptRequests } from 'store/thunks/workQueueThunks';
import { errorHandlerActions } from 'store/slices/errorHandlerSlice';

import perfectRxTheme from 'common/theme/perfectRxTheme';
import RxInTakeRequestView from './RxInTakeRequestView';
import RxInTakeRequestEditModal from './RxInTakeRequestEditModal';
import RxIntakePayloadViewDialog from './RxIntakePayloadViewDialog';

const Transition = forwardRef((props, ref) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Slide direction='up' ref={ref} {...props} timeout={300} />
));

const ORDER_REJECTED_BY_ACCEPTANCE_RULE = 'ORDER_REJECTED_BY_ACCEPTANCE_RULE';

/**
 * POM-6246 — the backend sends one positional reason slot per prescription, `{}` for a
 * prescription the rules are fine with. Only the values are read: the key is the portal's label
 * for the field a message belongs to, and a whole-state block has no field to point at.
 */
const buildRejectionMessage = (reasons) => {
  const messages = flatMap(reasons || [], (slot) => Object.values(slot || {})).filter(Boolean);

  return messages.length > 0
    ? messages.join(' ')
    : 'This order cannot be shipped to the requested destination.';
};

const RxInTakeRequestsOptions = ({ scriptId, scriptStatus, scriptPractice }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRawJsonModal, setRawJsonModal] = useState(false);
  const [showRequestView, setShowRequestView] = useState(false);
  const [showImportRequestModal, setShowImportRequestModal] = useState(false);
  const [showCloseRequestModal, setShowCloseRequestModal] = useState(false);
  const [showEditRequestModal, setShowEditRequestModal] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const { pages, currentPage } = useSelector(({ workQueue }) => workQueue.rxIntakeRequests);
  const scriptDetails = find(pages[currentPage], { scriptId });
  // Importing refetches the queue, and the row can leave the current page when its status changes
  // under a status filter. This renders once more before the grid drops it, so the payload has to
  // survive the row being gone.
  const { enrichedPrescriptionPayload, originalPrescriptionPayload, originalPayload } =
    scriptDetails?.requestPayload || {};
  const enableCancelOption = includes(['OPEN', 'HOLD'], scriptStatus);
  const enableImportOption = includes(['OPEN', 'IMPORT_FAILED'], scriptStatus);
  const enableEditOption = includes(['OPEN', 'HOLD', 'CLOSED'], scriptStatus);

  const [importPayload, setImportPayload] = useState(enrichedPrescriptionPayload);

  const menuOpen = !!anchorEl;
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleJsonView = () => setRawJsonModal((prev) => !prev);

  const openRequestDetailsView = () => setShowRequestView(true);
  const openCloseRequestModal = () => setShowCloseRequestModal(true);
  const openImportRequestModal = () => setShowImportRequestModal(true);
  const openEditRequestModal = () => setShowEditRequestModal(true);

  const normalizeDate = (dateInput) => {
    if (isDate(dateInput)) {
      return format(dateInput, 'yyyy-MM-dd');
    }

    const parsedDate = parseISO(dateInput);
    return isDate(parsedDate) ? format(parsedDate, 'yyyy-MM-dd') : null;
  };

  const closeViewRxIntakeRequestModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setShowRequestView(false);
    }
  };

  const closeEditRxIntakeRequestModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setShowEditRequestModal(false);
    }
  };

  const closeCancelRxIntakeRequestModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setShowCloseRequestModal(false);
    }
  };

  const closeImportRxIntakeRequestModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setShowImportRequestModal(false);
      setIsConfirmed(false);
    }
  };

  const handleImport = (data) => {
    setImportPayload(data);
    setShowImportRequestModal(true);
  };

  const handleCheckboxChange = (event) => {
    setIsConfirmed(event.target.checked);
  };

  const handleImportEnrichedRequest = () => {
    setLoading(true);
    const { partnerUuid, practiceId, partnerAccountId, patient, prescription } = importPayload;
    const { prescribedBy, transferDetails } = prescription;

    const input = {
      practiceId,
      eScriptId: scriptId,
      partnerId: partnerUuid,
      patientInput: {
        ...pick(patient, ['firstName', 'lastName', 'gender', 'phoneNumber']),
        email: patient.emailAddress,
        address: patient.patientAddress,
        dob: patient?.dob && normalizeDate(patient.dob),
      },
      shippingInput: patient.patientAddress,
      prescriptionInput: {
        ...pick(prescription, ['directions', 'notes']),
        strength: prescription.strength,
        quantity: toNumber(prescription.quantity),
        drugName: prescription.description,
        refills: toNumber(prescription.totalRefills),
        dispenseAsWritten: !!prescription?.dispenseAsWritten,
        dateWritten:
          transferDetails?.writtenDate &&
          format(new Date(transferDetails.writtenDate), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        ...(prescription?.needsByDate && {
          needsByDate: normalizeDate(importPayload.prescription.needsByDate),
        }),
        ...(prescription?.expirationDate && {
          expirationDate: normalizeDate(importPayload.prescription.expirationDate),
        }),
      },
      // POM-6361: no prescriber address is sent — core uses the practice address.
      providerInput: omit(prescribedBy, 'address'),
      ...(partnerAccountId && { partnerAccountId }),
    };

    dispatch(importEScriptRequests([input]))
      .then((result) => {
        // One result per script, and exactly one script is ever sent from here. A thrown error
        // resolves undefined through handleError, which is the third case: say nothing, the
        // global error handler already has.
        const [importResult] = result || [];

        if (!importResult) {
          return;
        }

        // POM-6246 — a script the order acceptance rules refuse never became an order, so it
        // must not be reported as one.
        //
        // The reason goes to the app-level error dialog rather than one owned by this row.
        // Importing refetches the queue, and the table unmounts its rows while that is in
        // flight, so a dialog held in this component's state is torn down before anyone reads
        // it — which is exactly what QA saw on 09-22: no false success any more, but no reason
        // either. The global dialog outlives the row, and getErrorMessage already allow-lists
        // this code to print the backend's own copy verbatim.
        if (importResult.status === 'REJECTED') {
          closeImportRxIntakeRequestModal();
          dispatch(
            errorHandlerActions.setError({
              error: {
                errorHeader: 'Order Not Imported',
                code: ORDER_REJECTED_BY_ACCEPTANCE_RULE,
                message: buildRejectionMessage(importResult.reasons),
              },
            })
          );
          return;
        }

        closeViewRxIntakeRequestModal();
        closeImportRxIntakeRequestModal();
        enqueueSnackbar('Order Successfully Imported', { variant: 'success' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const closeEnrichedRequests = () => {
    setLoading(true);

    dispatch(closeEScriptRequests([scriptId]))
      .then((result) => {
        if (result) {
          closeViewRxIntakeRequestModal();
          closeCancelRxIntakeRequestModal();
          enqueueSnackbar('Order Successfully Closed', { variant: 'success' });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const displayPracticeWarning = () => {
    if (lowerCase(scriptPractice) === lowerCase(importPayload.practiceName)) {
      return false;
    }

    if (scriptPractice && importPayload?.practiceName) {
      const fuse = new Fuse([scriptPractice], { includeScore: true });
      const [result] = fuse.search(importPayload?.practiceName);

      const thresholdScore = process.env.REACT_APP_TEXT_MATCH_THRESHOLD
        ? Number(process.env.REACT_APP_TEXT_MATCH_THRESHOLD)
        : 0.4;

      return !result?.score || result?.score < thresholdScore;
    }

    return false;
  };

  return (
    <>
      {/* View Enriched Request */}
      <Dialog fullScreen open={showRequestView} TransitionComponent={Transition}>
        <Grid container sx={{ px: 4, pt: 1 }} justifyContent='flex-end'>
          <IconButton
            onClick={closeViewRxIntakeRequestModal}
            id={`Close RxInTakeRequestView - ${scriptId}`}
            aria-label='Close RxInTakeRequestView Button'
          >
            <CloseIcon fontSize='medium' />
          </IconButton>
        </Grid>

        <Grid container sx={{ px: 5 }}>
          <Grid container sx={{ p: 0.5 }} justifyContent='space-between' alignItems='center'>
            <Grid item>
              <Typography
                variant='h2'
                sx={{ color: lighten(theme.palette.text.primary, 0.3), ml: 3 }}
              >
                Pharmacy Order
              </Typography>
            </Grid>

            <Grid item>
              <Grid container justifyContent='flex-end' alignItems='center' columnGap={1}>
                {enableCancelOption && (
                  <Button variant='outlined' color='error' onClick={openCloseRequestModal}>
                    Close
                  </Button>
                )}

                <Button variant='outlined' color='secondary' onClick={handleJsonView}>
                  View Original Data
                </Button>

                {enableEditOption && scriptStatus !== 'CLOSED' && (
                  <Button variant='outlined' color='secondary' onClick={openEditRequestModal}>
                    Edit Details
                  </Button>
                )}

                {enableImportOption && (
                  <Button variant='contained' color='primary' onClick={openImportRequestModal}>
                    Import
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ mx: 6 }} />
        <DialogContent sx={{ p: 0 }}>
          <RxInTakeRequestView scriptId={scriptId} />
        </DialogContent>
      </Dialog>

      {/* Close Request */}
      <Dialog maxWidth='sm' open={showCloseRequestModal} TransitionComponent={Transition}>
        <DialogTitle variant='h5'>Close Order</DialogTitle>
        <DialogContent sx={{ minWidth: 450 }}>
          <Typography variant='subtitle1' sx={{ mb: 2 }}>
            Please confirm that the order should not be imported.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='secondary' onClick={closeCancelRxIntakeRequestModal}>
            Cancel
          </Button>
          <LoadingButton loading={loading} variant='contained' onClick={closeEnrichedRequests}>
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Import Request */}
      <Dialog maxWidth='sm' open={showImportRequestModal} TransitionComponent={Transition}>
        <DialogTitle>Confirm Order Import</DialogTitle>
        <DialogContent>
          <Typography variant='body1' gutterBottom>
            Please confirm the following details before importing the order:
          </Typography>

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Typography variant='subtitle2'>Patient:</Typography>
            <Typography variant='body2' fontWeight='bold'>
              {importPayload.patient.lastName}, {importPayload.patient.firstName}
            </Typography>
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Typography variant='subtitle2'>Partner:</Typography>
            <Typography variant='body2' fontWeight='bold'>
              {importPayload.partnerName}
            </Typography>
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                px: 0.5,
                ...(displayPracticeWarning() && {
                  bgcolor: perfectRxTheme.palette.warning.warningColour,
                }),
              }}
            >
              <Typography variant='subtitle2'>Practice:</Typography>
              <Typography variant='body2' fontWeight='bold'>
                {importPayload.practiceName}
              </Typography>
            </Box>

            {displayPracticeWarning() && (
              <Tooltip
                placement='right'
                arrow
                title={`Practice received from the original request(${scriptPractice}) does not match with the practice selected for import`}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                  <WarningIcon sx={{ color: perfectRxTheme.palette.warning.warningColour }} />
                </Box>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Typography variant='subtitle2'>Partner Account:</Typography>
            <Typography variant='body2' fontWeight='bold'>
              {importPayload.partnerAccount || 'NA'}
            </Typography>
          </Box>

          <Alert severity='info' sx={{ mt: 3, alignItems: 'center' }}>
            Please ensure all details are accurate before proceeding with the import
          </Alert>

          <FormControlLabel
            control={<Checkbox checked={isConfirmed} onChange={handleCheckboxChange} />}
            label='I confirm that I have reviewed the above details and that all information is accurate'
            sx={{ mt: 3 }}
          />
        </DialogContent>

        <DialogActions>
          <Button variant='outlined' color='secondary' onClick={closeImportRxIntakeRequestModal}>
            Cancel
          </Button>
          <LoadingButton
            loading={loading}
            variant='contained'
            disabled={!isConfirmed}
            onClick={handleImportEnrichedRequest}
          >
            Import
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* View Original Request Payload */}
      <RxIntakePayloadViewDialog
        open={showRawJsonModal}
        jsonData={originalPayload}
        originalPrescriptionPayload={originalPrescriptionPayload}
        closeModel={handleJsonView}
      />

      {/* Edit Request */}
      {showEditRequestModal && (
        <RxInTakeRequestEditModal
          scriptId={scriptId}
          showModal={showEditRequestModal}
          onClose={closeEditRxIntakeRequestModal}
          handleImport={handleImport}
        />
      )}

      <IconButton
        id={`WorkQueue-RxInTakeRequests-${scriptId}options-button`}
        aria-label={`RxInTakeRequests options ${scriptId}`}
        aria-controls={menuOpen ? `WorkQueue-RxInTakeRequests-${scriptId}options-menu` : undefined}
        aria-haspopup='true'
        aria-expanded={menuOpen ? 'true' : undefined}
        onClick={handleMenuOpen}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        id={`WorkQueue-RxInTakeRequests-${scriptId}options-menu`}
        aria-labelledby={`WorkQueue-RxInTakeRequests-${scriptId}options-button`}
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={openRequestDetailsView}>View</MenuItem>
        {enableCancelOption && <MenuItem onClick={openCloseRequestModal}>Close</MenuItem>}
        {enableImportOption && <MenuItem onClick={openImportRequestModal}>Import</MenuItem>}
      </Menu>
    </>
  );
};

RxInTakeRequestsOptions.propTypes = {
  scriptId: PropTypes.string.isRequired,
  scriptStatus: PropTypes.string.isRequired,
  scriptPractice: PropTypes.string,
};

RxInTakeRequestsOptions.defaultProps = {
  scriptPractice: undefined,
};

export default RxInTakeRequestsOptions;
